import { produce } from 'immer';
import { AnyAction } from 'redux';
import { SiteWidgetsFleetDashboardActions } from './siteWidgetsFleetDashboardActions';
import { Optional } from 'lib/types/Optional';
import { SiteWithOperatingTime } from 'app/modules/site-management/interfaces/Site.types';
import { SiteOperatingTimePeriod } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { END_DATE, OperatingHoursChartUtils, START_DATE } from 'app/modules/cleaning/utils/OperatingHoursChartUtils';

export interface SiteWidgetsFleetDashboardState {
  data: Optional<SiteWithOperatingTime[]>;
  /** Aggregated operating time for the currently selected "no site assigned" machines. */
  noSiteMachinesOperatingTime: Optional<SiteOperatingTimePeriod>;
  isLoading: boolean;
  userHasInteracted: boolean;
  filters: {
    period: {
      startDate: string;
      endDate: string;
    };
  };
}

export const initialState: SiteWidgetsFleetDashboardState = {
  data: null,
  noSiteMachinesOperatingTime: null,
  isLoading: false,
  userHasInteracted: false,
  filters: {
    period: {
      startDate: OperatingHoursChartUtils.prepareStartDate(START_DATE).toISOString(),
      endDate: OperatingHoursChartUtils.prepareStartDate(END_DATE).toISOString(),
    },
  },
};

export const siteWidgetsFleetDashboardReducer = (
  state = initialState,
  action: AnyAction
): SiteWidgetsFleetDashboardState =>
  produce(state, draft => {
    switch (action.type) {
      case SiteWidgetsFleetDashboardActions.GET_SITE_LIST_WITH_OPERATING_TIME_REQUEST: {
        draft.isLoading = true;
        return draft;
      }

      case SiteWidgetsFleetDashboardActions.GET_SITE_LIST_WITH_OPERATING_TIME_SUCCESS: {
        const {
          sites: { data },
          noSiteMachinesOperatingTime,
        } = action.payload;
        draft.isLoading = false;
        draft.userHasInteracted = false;
        draft.data = data;
        draft.noSiteMachinesOperatingTime = noSiteMachinesOperatingTime ?? null;
        return draft;
      }

      case SiteWidgetsFleetDashboardActions.GET_SITE_LIST_WITH_OPERATING_TIME_ERROR: {
        draft.isLoading = false;
        draft.userHasInteracted = false;
        return draft;
      }

      case SiteWidgetsFleetDashboardActions.RESET_STATE: {
        draft.isLoading = false;
        draft.userHasInteracted = false;
        return draft;
      }

      case SiteWidgetsFleetDashboardActions.SET_PERIOD_FILTER: {
        const { period } = action.payload;

        draft.filters.period = period;
        draft.userHasInteracted = true;
        return draft;
      }

      default:
        return draft;
    }
  });
